import { html, LitElement } from 'lit';
import { _converse, u } from '@converse/headless';
import { getMeetBaseUrl } from '../../utils/conference.js';

/**
 * Floating Jitsi conference overlay.
 *
 * Properties:
 *   room        {string} – Jitsi room name (MUC JID local part)
 *   mucJid      {string} – full MUC JID, used to send status messages
 *   displayName {string} – current user display name
 *   subject     {string} – human-readable group name shown as conference title
 */
class JitsiConference extends LitElement {
    static properties = {
        room:          { type: String },
        mucJid:        { type: String },
        displayName:   { type: String },
        subject:       { type: String },   // Bug 3: group display name for Jitsi title
        isOrganizer:   { type: Boolean },
        _loaded:       { type: Boolean, state: true },
        _error:        { type: String,  state: true },
        _isModerator:  { type: Boolean, state: true },
        _urlCopied:    { type: Boolean, state: true }, // Bug 4: copy URL feedback
    };

    createRenderRoot() { return this; }

    constructor() {
        super();
        this.room = '';
        this.mucJid = '';
        this.displayName = '';
        this.subject = '';
        this.isOrganizer = false;
        this._loaded = false;
        this._error = null;
        this._jitsiApi = null;
        this._myId = null;
        this._participants = new Set();
        this._userInitiatedLeave = false;
        this._urlCopied = false;
        this._closed = false;
        // Bug 1: MUC watcher refs
        this._mucChatbox = null;
        this._mucKickedListener = null;
        this._mucOccupantListener = null;
    }

    connectedCallback() {
        super.connectedCallback();
        this._loadAndInit();
        this._watchMucStatus(); // Bug 1
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this._jitsiApi?.dispose();
        this._jitsiApi = null;
        // Bug 1: cleanup MUC watchers
        if (this._mucChatbox) {
            this._mucChatbox.off('kicked', this._mucKickedListener);
            this._mucChatbox.occupants?.off('change:affiliation', this._mucOccupantListener);
        }
    }

    // Bug 1: close conference if user is kicked or removed from MUC
    _watchMucStatus() {
        if (!this.mucJid) return;
        const chatbox = _converse.chatboxes?.get(this.mucJid);
        if (!chatbox) return;

        this._mucChatbox = chatbox;

        this._mucKickedListener = () => {
            console.warn('[JitsiConference] Kicked from MUC — closing conference');
            this._close();
        };
        chatbox.on('kicked', this._mucKickedListener);

        // Also watch affiliation: 'none' = removed, 'outcast' = banned
        this._mucOccupantListener = (occupant) => {
            const bare_jid = _converse.session?.get('bare_jid');
            if (occupant.get('jid') !== bare_jid) return;
            const aff = occupant.get('affiliation');
            if (aff === 'none' || aff === 'outcast') {
                console.warn('[JitsiConference] Own affiliation removed — closing conference');
                this._close();
            }
        };
        chatbox.occupants?.on('change:affiliation', this._mucOccupantListener);
    }

    async _loadAndInit() {
        const meetBase = getMeetBaseUrl();
        if (!meetBase) {
            this._error = 'meet_url not configured';
            return;
        }

        if (!window.JitsiMeetExternalAPI) {
            await new Promise((resolve, reject) => {
                const script = document.createElement('script');
                // Vendored locally (3rdparty/jitsi-external_api.js) instead of
                // fetched from meetBase at runtime: this app needs to keep
                // working behind NATs/firewalls that block reaching the Jitsi
                // server for anything other than the actual call signaling.
                // Relative path (no leading slash) so it resolves correctly
                // under both http(s):// (web) and file:// (Electron) — see
                // assets_path in sonata-auth.js for the same lesson learned
                // the hard way.
                script.src = '3rdparty/jitsi-external_api.js';
                script.onload = resolve;
                script.onerror = () => reject(new Error(`Не вдалося завантажити Jitsi SDK: ${script.src}`));
                document.head.appendChild(script);
            }).catch((e) => { this._error = e.message; });
        }

        if (this._error || !window.JitsiMeetExternalAPI) return;

        this._loaded = true;
        // Bug 4: update browser URL hash so the room ID is visible/shareable
        this._originalHash = window.location.hash;
        const meetBase2 = getMeetBaseUrl();
        if (meetBase2 && this.room) {
            history.replaceState(null, '', `#conference/${this.room}`);
        }
        await this.updateComplete;

        const container = this.querySelector('#jitsi-frame-container');
        if (!container) return;

        const domain = meetBase.replace(/^https?:\/\//, '');

        this._jitsiApi = new window.JitsiMeetExternalAPI(domain, {
            roomName: this.room,
            parentNode: container,
            userInfo: { displayName: this.displayName || '' },
            configOverwrite: {
                startWithAudioMuted: false,
                startWithVideoMuted: false,
                disableDeepLinking: true,
                prejoinPageEnabled: false,
                enableWelcomePage: false,
                // Bug 3: pre-set conference subject via config
                subject: this.subject || undefined,
            },
            interfaceConfigOverwrite: {
                SHOW_JITSI_WATERMARK: false,
                SHOW_WATERMARK_FOR_GUESTS: false,
                SHOW_BRAND_WATERMARK: false,
                SHOW_CHROME_EXTENSION_BANNER: false,
                TOOLBAR_BUTTONS: [
                    'microphone', 'camera', 'desktop',
                    'tileview', 'fullscreen', 'settings',
                ],
            },
        });

        this._jitsiApi.addListener('videoConferenceJoined', ({ id }) => {
            this._myId = id;
            this._checkModeratorRole();
            // Bug 3: set human-readable conference title after join
            if (this.subject) {
                try { this._jitsiApi.executeCommand('subject', this.subject); } catch(_) {}
            }
        });

        this._jitsiApi.addListener('participantJoined', ({ id }) => this._participants.add(id));
        this._jitsiApi.addListener('participantLeft',   ({ id }) => this._participants.delete(id));

        // Bug 2: ignore Jitsi moderator role changes — only the call initiator
        // (isOrganizer=true) should see "Завершити для всіх". Auto-granting
        // moderator to every first joiner caused non-initiators to get the button.

        this._jitsiApi.addListener('videoConferenceLeft', () => {
            if (!this._userInitiatedLeave) {
                // TODO(spec pitfall): this fires identically for "room was
                // destroyed" and for a plain network/ICE disconnect -- the
                // spec requires distinguishing them via the error condition
                // (strictly conference.destroyed, not iceFailed /
                // videobridgeNotAvailable / gracefulShutdown / focusLeft),
                // otherwise a flaky connection wrongly announces a live call
                // as over for everyone. This vendored JitsiMeetExternalAPI
                // build's videoConferenceLeft payload wasn't confirmed to
                // carry that distinction -- needs verifying against a live
                // call before trusting it either way, so for now we still
                // send unconditionally, matching the pre-existing behaviour.
                this._sendConferenceEndedEvent();
            }
            this._close();
        });

        this._jitsiApi.addListener('readyToClose', () => {
            if (!this._userInitiatedLeave) {
                this._sendConferenceEndedEvent();
            }
            this._close();
        });
    }

    _checkModeratorRole() {
        // Bug 2: only the call initiator (isOrganizer) gets "Завершити для всіх"
        if (this.isOrganizer) {
            this._isModerator = true;
            this.requestUpdate();
        }
    }

    _handleLeave() {
        this._userInitiatedLeave = true;
        const wasLast = this._participants.size === 0;
        // Only announce when the call has fully ended, not every individual
        // participant leaving (that message got sent once per person and
        // cluttered the chat) — "ended" is also relied on by heading.js to
        // detect whether a later join should post a fresh "started" message.
        if (wasLast) {
            this._sendConferenceEndedEvent();
        }
        this._jitsiApi?.executeCommand('hangup');
    }

    // Bug 2: guarantee the overlay closes — endConference may silently fail if
    // the Jitsi server hasn't granted moderator role, so always fall back to
    // hangup + _close() after 500 ms.
    _handleEndForAll() {
        this._userInitiatedLeave = true;
        this._sendConferenceEndedEvent();
        try { this._jitsiApi?.executeCommand('endConference'); } catch(_) {}
        // Fallback: if endConference doesn't trigger videoConferenceLeft, close manually
        this._jitsiApi?.executeCommand('hangup');
        setTimeout(() => this._close(), 500);
    }

    // Bug 4: copy the Jitsi conference URL to clipboard
    async _copyUrl() {
        const meetBase = getMeetBaseUrl();
        const url = `${meetBase}/${this.room}`;
        try {
            await navigator.clipboard.writeText(url);
            this._urlCopied = true;
            setTimeout(() => { this._urlCopied = false; this.requestUpdate(); }, 2000);
            this.requestUpdate();
        } catch(e) {
            console.warn('[JitsiConference] Could not copy URL:', e);
        }
    }

    // Sent as the structured sonata:groupevent:0 protocol (fixed English
    // wire body, impersonal -- no actor) rather than a plain chat message,
    // so every updated client renders the same localized, unattributed line
    // instead of this literal Ukrainian text (spec: conference-ended has no
    // actor since whoever reports the end isn't necessarily who ended it).
    _sendConferenceEndedEvent() {
        if (!this.mucJid) return;
        const chatbox = _converse.chatboxes?.get(this.mucJid);
        if (!chatbox) return;
        try {
            u.muc.sendGroupEvent(chatbox, 'conference-ended');
        } catch (e) {
            console.warn('[JitsiConference] Could not send conference-ended event:', e);
        }
    }

    _close() {
        if (this._closed) return; // guard against double-close
        this._closed = true;
        this._jitsiApi?.dispose();
        this._jitsiApi = null;
        // Bug 4: restore original URL hash
        if (this._originalHash !== undefined) {
            history.replaceState(null, '', this._originalHash || window.location.pathname);
        }
        this.remove();
    }

    _renderLoading() {
        return html`<div style="position:fixed;inset:0;z-index:9999;background:rgba(10,20,45,.97);display:flex;align-items:center;justify-content:center;flex-direction:column;gap:16px"><div style="color:#9ab9ff;font-size:15px">Підключення до конференції…</div></div>`;
    }

    _renderError() {
        return html`<div style="position:fixed;inset:0;z-index:9999;background:rgba(0,0,0,.85);display:flex;align-items:center;justify-content:center"><div style="color:#fff;text-align:center;padding:24px;max-width:360px"><p style="font-size:16px;margin-bottom:16px">⚠️ ${this._error}</p><button @click="${() => this.remove()}" style="padding:8px 20px;border-radius:8px;border:none;background:#e05252;color:#fff;cursor:pointer;font-size:14px">Закрити</button></div></div>`;
    }

    render() {
        if (this._error)  return this._renderError();
        if (!this._loaded) return this._renderLoading();

        const meetBase = getMeetBaseUrl();
        const confUrl = meetBase ? `${meetBase}/${this.room}` : '';
        const btnBase = 'border:none;border-radius:8px;padding:8px 16px;font-size:14px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:6px;';

        return html`<div style="position:fixed;inset:0;z-index:9999;background:#000;display:flex;flex-direction:column"><div style="position:absolute;bottom:0;left:0;right:0;z-index:10000;padding:12px 16px;display:flex;align-items:center;justify-content:center;gap:12px;flex-wrap:wrap;background:linear-gradient(transparent,rgba(0,0,0,.75))">${confUrl ? html`<div style="display:flex;align-items:center;gap:6px;background:rgba(255,255,255,.1);border-radius:8px;padding:4px 10px;font-size:12px;color:#bbb;max-width:260px;overflow:hidden"><span style="flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-family:monospace">${confUrl}</span> <button @click="${() => this._copyUrl()}" style="border:none;background:0 0;color:${this._urlCopied ? '#4caf50' : '#9ab9ff'};cursor:pointer;padding:0 2px;font-size:14px;flex-shrink:0;line-height:1" title="Копіювати посилання на конференцію">${this._urlCopied ? '✓' : '📋'}</button></div>` : ''} <button @click="${() => this._handleLeave()}" style="${btnBase}background:#e05252;color:#fff" title="Залишити конференцію">📞 Вийти</button> ${this._isModerator ? html`<button @click="${() => this._handleEndForAll()}" style="${btnBase}background:#c0392b;color:#fff;border:2px solid #ff6b6b" title="Завершити конференцію для всіх">🛑 Завершити для всіх</button>` : ''}</div><div id="jitsi-frame-container" style="flex:1;width:100%;height:100%;padding-bottom:64px;box-sizing:border-box"></div></div>`;
    }
}

customElements.define('converse-jitsi-conference', JitsiConference);
